import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'

import { NpvMark } from '@/components/generals/npv-mark'
import { cn } from '@/lib/utils'

import { navLinks } from '@/constants/nav'
import { siteConfig } from '@/constants/site'
import { SOS } from '@/constants/canais'

export const Navbar: FC = (): ReactNode => {
    const router = useRouter()

    const [scrolled, setScrolled] = useState<boolean>(false)
    const [open, setOpen] = useState<boolean>(false)

    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 24)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        if (!open) return

        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        return () => {
            document.body.style.overflow = previous
        }
    }, [open])

    useEffect(() => {
        const onRoute = (): void => setOpen(false)

        router.events.on('routeChangeComplete', onRoute)

        return () => router.events.off('routeChangeComplete', onRoute)
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const onKey = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [open])

    const isActive = (href: string): boolean => {
        if (href === '/') return router.pathname === '/'

        return router.pathname === href || router.pathname.startsWith(`${href}/`)
    }

    return (
        <header className={cn('nav-root', scrolled && 'nav-scrolled', open && 'nav-open')}>
            <Link
                href='/'
                className='nav-mark'
                aria-label={`${siteConfig.name}, ${siteConfig.fullName}, página inicial`}
            >
                <NpvMark className='nav-mark-icon' />

                <span>
                    {siteConfig.name}
                </span>
            </Link>

            <nav className='nav-links' aria-label="Principal">
                {navLinks.map(link => (
                    <Link
                        key={link.href}
                        href={link.href}
                        className={cn('nav-link', isActive(link.href) && 'nav-link-active')}
                        aria-current={isActive(link.href) ? 'page' : undefined}
                    >
                        <span className='nav-link-text'>
                            {link.label}
                        </span>
                    </Link>
                ))}
            </nav>

            <button
                type='button'
                className='nav-toggle'
                aria-expanded={open}
                aria-controls='nav-takeover'
                onClick={() => setOpen(value => !value)}
            >
                {open ? "Fechar" : "Menu"}
            </button>

            <div
                id='nav-takeover'
                className='nav-takeover'
                aria-hidden={!open}
                hidden={!open}
            >
                <nav className='nav-takeover-list' aria-label="Principal, expandido">
                    {navLinks.map((link, i) => (
                        <Link
                            key={link.href}
                            href={link.href}
                            className={cn(
                                'nav-takeover-link',
                                isActive(link.href) && 'nav-takeover-link-active'
                            )}
                            style={{ animationDelay: `${0.06 + i * 0.07}s` }}
                        >
                            <span className='nav-takeover-index'>
                                {String(i + 1).padStart(2, '0')}
                            </span>

                            {link.label}
                        </Link>
                    ))}
                </nav>

                <div className='nav-takeover-foot'>
                    <p className='nav-takeover-foot-line'>
                        Em perigo, ligue
                    </p>

                    <ul className='nav-takeover-sos'>
                        {SOS.slice(0, 4).map(item => (
                            <li key={item.numero}>
                                <a href={item.tel}>
                                    <strong>
                                        {item.numero}
                                    </strong>

                                    {item.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </header>
    )
}
